import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Shield,
  RefreshCw,
  ChevronLeft,
  Save,
  ShieldCheck,
  ShieldAlert,
  Users,
  Edit3,
  Hash,
  Activity,
  UserPlus,
} from "lucide-react";

// ✅ Hooks & Utils
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";

import {
  useAssignMemberToOfficeMutation,
  useGetExecutiveOfficeDetailsQuery,
  useUpdateExecutiveOfficeMutation,
} from "../api/executiveOfficeApi";

import RolePrivilegesEditor from "../../access/components/RolePrivilegesEditor";

type UiMsg = { type: "success" | "error"; text: string } | null;

function errMsg(err: any) {
  return err?.data?.message || err?.error || "Request failed";
}

function fullName(m: any) {
  return (
    [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—"
  );
}

export default function ExecutiveOfficeDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const officeId = Number(id);
  const { can } = useAccess();

  const { data, isLoading, error, refetch } = useGetExecutiveOfficeDetailsQuery(
    officeId,
    { skip: !officeId }
  );

  const [updateOffice, { isLoading: saving }] =
    useUpdateExecutiveOfficeMutation();
  const [assignMember, { isLoading: assigning }] =
    useAssignMemberToOfficeMutation();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [edit, setEdit] = useState({ name: "", description: "", rank: "" });
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (data) {
      setEdit({
        name: data.name ?? "",
        description: data.description ?? "",
        rank: data.rank != null ? String(data.rank) : "",
      });
    }
  }, [data]);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!edit.name.trim())
      return setMsg({ type: "error", text: "Office name is required" });

    try {
      await updateOffice({
        id: officeId,
        name: edit.name.trim(),
        description: edit.description?.trim() || null,
        rank: edit.rank ? Number(edit.rank) : null,
      }).unwrap();
      setMsg({ type: "success", text: "Office updated successfully" });
      refetch();
    } catch (err) {
      setMsg({ type: "error", text: errMsg(err) });
    }
  };

  if (isLoading)
    return (
      <div className="p-10 text-center animate-pulse text-gray-400 font-medium">
        Loading Office Architecture...
      </div>
    );

  return (
    <PageGate perm={PERMS.executive_office?.detailsAccess}>
      <div className="space-y-6 pb-20">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-900 rounded-xl shadow-lg shadow-gray-200">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <button
                  onClick={() => navigate("/executive-offices")}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-1"
                >
                  <ChevronLeft className="w-3 h-3" /> BACK TO OFFICES
                </button>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Office Intelligence
                </h1>
                <p className="text-sm text-gray-500">
                  Managing Profile:{" "}
                  <span className="font-bold text-gray-800">{data?.name}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="p-2.5 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 text-gray-600 ${
                    isLoading ? "animate-spin" : ""
                  }`}
                />
              </button>

              <AccessGate perm={PERMS.executive_office?.update}>
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-gray-900/20"
                >
                  <Save className="w-4 h-4" />{" "}
                  {saving ? "SAVING..." : "UPDATE OFFICE"}
                </button>
              </AccessGate>
            </div>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-xl border text-sm font-medium animate-in fade-in slide-in-from-top-2 ${
                msg.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <div className="flex items-center gap-2">
                {msg.type === "success" ? (
                  <ShieldCheck className="w-4 h-4" />
                ) : (
                  <ShieldAlert className="w-4 h-4" />
                )}
                {msg.text}
              </div>
            </div>
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Summary Stats */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-2xl p-6 shadow-sm flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Office Slug
                </div>
                <div className="text-lg font-bold text-blue-600 mt-1 font-mono">
                  {data?.slug || "---"}
                </div>
              </div>
              <Shield className="w-8 h-8 text-blue-100" />
            </div>
            <div className="bg-white border rounded-2xl p-6 shadow-sm flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Hierarchy Rank
                </div>
                <div className="text-lg font-bold text-gray-900 mt-1">
                  Level {data?.rank || "0"}
                </div>
              </div>
              <Hash className="w-8 h-8 text-gray-100" />
            </div>

            {/* Live Editor Box */}
            <div className="md:col-span-2 bg-white border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b pb-4">
                <Edit3 className="w-4 h-4 text-gray-400" />
                <h2 className="text-sm font-black text-gray-900 uppercase">
                  Configuration
                </h2>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-500 uppercase">
                      Display Name
                    </label>
                    <input
                      value={edit.name}
                      onChange={(e) =>
                        setEdit({ ...edit, name: e.target.value })
                      }
                      className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-gray-900/5 transition-all font-medium"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-500 uppercase">
                      Rank Order
                    </label>
                    <input
                      type="number"
                      value={edit.rank}
                      onChange={(e) =>
                        setEdit({ ...edit, rank: e.target.value })
                      }
                      className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-gray-900/5 transition-all font-medium"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-500 uppercase">
                    Responsibility Description
                  </label>
                  <textarea
                    value={edit.description}
                    onChange={(e) =>
                      setEdit({ ...edit, description: e.target.value })
                    }
                    rows={3}
                    className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-gray-900/5 transition-all font-medium resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Member Assignment Sidebar */}
          <div className="bg-gray-900 rounded-3xl p-6 text-white shadow-xl shadow-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-black text-lg tracking-tight">
                Active Occupants
              </h2>
              <Users className="w-5 h-5 text-gray-500" />
            </div>

            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {data?.executives?.map((ex: any) => (
                <div
                  key={ex.id}
                  className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div className="font-bold text-sm">{fullName(ex.member)}</div>
                  <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">
                    {ex.executive_set?.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full border border-blue-500/30">
                      {ex.position || "Standard"}
                    </span>
                  </div>
                </div>
              ))}
              {!data?.executives?.length && (
                <div className="text-center py-10 text-gray-500 text-xs italic">
                  No members currently assigned
                </div>
              )}
            </div>

            <AccessGate perm={PERMS.executive_office?.assign}>
              <button className="w-full mt-6 py-3 bg-white text-gray-900 rounded-xl font-bold text-xs uppercase hover:bg-gray-100 transition-all flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" /> Assign New Member
              </button>
            </AccessGate>
          </div>
        </div>

        {/* Permissions / Matrix Section */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-[1px] flex-1 bg-gray-100"></div>
            <h2 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.3em]">
              Access Matrix & Privileges
            </h2>
            <div className="h-[1px] flex-1 bg-gray-100"></div>
          </div>

          {data?.role_id ? (
            <RolePrivilegesEditor roleId={data.role_id} />
          ) : (
            <div className="bg-white border-2 border-dashed border-gray-100 rounded-3xl p-20 text-center">
              <ShieldAlert className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 font-medium">
                This office has no security role attached.
              </p>
              <p className="text-xs text-gray-300 mt-1">
                Access privileges cannot be defined without a role mapping.
              </p>
            </div>
          )}
        </div>
      </div>
    </PageGate>
  );
}
